/**
 * Shared light theme for Shipment & External Commission dashboards.
 * Scoped to these pages only — does not affect global UI tokens.
 */

export const COMMISSION_PALETTE = {
  sky: {
    summary: 'bg-gradient-to-br from-sky-50 to-white border border-sky-100',
    summaryIcon: 'text-sky-500 bg-sky-100/70',
    summaryMeta: 'border-sky-100/80 text-sky-700/80',
    tier: 'bg-sky-50/90 border border-sky-100 text-gray-800 hover:bg-sky-50 hover:border-sky-200 hover:shadow-sm',
    tierSelected: 'ring-2 ring-sky-200 ring-offset-1 border-sky-200',
    tierIcon: 'bg-sky-100 text-sky-600',
    tierDivider: 'border-sky-100/80',
    panelHeader: 'bg-sky-50 border-b border-sky-100 text-sky-900',
    panelClose: 'hover:bg-sky-100 text-sky-600',
    accent: 'text-sky-700',
    filterActive: 'bg-sky-50 text-sky-700',
    filterBadge: 'bg-sky-50 text-sky-700 border border-sky-100',
  },
  emerald: {
    summary: 'bg-gradient-to-br from-emerald-50 to-white border border-emerald-100',
    summaryIcon: 'text-emerald-500 bg-emerald-100/70',
    summaryMeta: 'border-emerald-100/80 text-emerald-700/80',
    tier: 'bg-emerald-50/90 border border-emerald-100 text-gray-800 hover:bg-emerald-50 hover:border-emerald-200 hover:shadow-sm',
    tierSelected: 'ring-2 ring-emerald-200 ring-offset-1 border-emerald-200',
    tierIcon: 'bg-emerald-100 text-emerald-600',
    tierDivider: 'border-emerald-100/80',
    panelHeader: 'bg-emerald-50 border-b border-emerald-100 text-emerald-900',
    panelClose: 'hover:bg-emerald-100 text-emerald-600',
    accent: 'text-emerald-700',
    filterActive: 'bg-emerald-50 text-emerald-700',
    filterBadge: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
  },
  violet: {
    summary: 'bg-gradient-to-br from-violet-50 to-white border border-violet-100',
    summaryIcon: 'text-violet-500 bg-violet-100/70',
    summaryMeta: 'border-violet-100/80 text-violet-700/80',
    tier: 'bg-violet-50/90 border border-violet-100 text-gray-800 hover:bg-violet-50 hover:border-violet-200 hover:shadow-sm',
    tierSelected: 'ring-2 ring-violet-200 ring-offset-1 border-violet-200',
    tierIcon: 'bg-violet-100 text-violet-600',
    tierDivider: 'border-violet-100/80',
    panelHeader: 'bg-violet-50 border-b border-violet-100 text-violet-900',
    panelClose: 'hover:bg-violet-100 text-violet-600',
    accent: 'text-violet-700',
    filterActive: 'bg-violet-50 text-violet-700',
    filterBadge: 'bg-violet-50 text-violet-700 border border-violet-100',
  },
  amber: {
    summary: 'bg-gradient-to-br from-amber-50 to-white border border-amber-100',
    summaryIcon: 'text-amber-500 bg-amber-100/70',
    summaryMeta: 'border-amber-100/80 text-amber-700/80',
    tier: 'bg-amber-50/90 border border-amber-100 text-gray-800 hover:bg-amber-50 hover:border-amber-200 hover:shadow-sm',
    tierSelected: 'ring-2 ring-amber-200 ring-offset-1 border-amber-200',
    tierIcon: 'bg-amber-100 text-amber-600',
    tierDivider: 'border-amber-100/80',
    panelHeader: 'bg-amber-50 border-b border-amber-100 text-amber-900',
    panelClose: 'hover:bg-amber-100 text-amber-600',
    accent: 'text-amber-700',
    filterActive: 'bg-amber-50 text-amber-700',
    filterBadge: 'bg-amber-50 text-amber-700 border border-amber-100',
  },
};

export const COMMISSION_STATUS_BADGE = {
  paid: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
  partial: 'bg-amber-50 text-amber-700 border border-amber-100',
  pending: 'bg-rose-50 text-rose-700 border border-rose-100',
};

export const COMMISSION_FINANCIAL = {
  total: 'bg-sky-50/80 border border-sky-100 text-sky-700',
  paid: 'bg-emerald-50/80 border border-emerald-100 text-emerald-700',
  pending: 'bg-rose-50/80 border border-rose-100 text-rose-700',
  neutral: 'bg-slate-50/80 border border-slate-100 text-slate-700',
};

export function getCommissionStatusBadge(status) {
  if (status === 'Paid' || status === 'PAID') return COMMISSION_STATUS_BADGE.paid;
  if (status === 'Partial' || status === 'PARTIAL') return COMMISSION_STATUS_BADGE.partial;
  return COMMISSION_STATUS_BADGE.pending;
}

export function getSummaryCardClasses(paletteKey) {
  return COMMISSION_PALETTE[paletteKey]?.summary || COMMISSION_PALETTE.sky.summary;
}

export function getTierClasses(paletteKey, selected) {
  const p = COMMISSION_PALETTE[paletteKey] || COMMISSION_PALETTE.sky;
  return `${p.tier} transition-all md:transform md:hover:scale-[1.005] ${selected ? p.tierSelected : ''}`;
}

export function getFilterActiveClasses(paletteKey = 'sky') {
  const p = COMMISSION_PALETTE[paletteKey] || COMMISSION_PALETTE.sky;
  return p.filterActive;
}

/** Paid/Pending row: stacked on mobile, side-by-side from md (iPad + desktop). */
export const COMMISSION_SUMMARY_META_ROW = 'flex flex-col gap-0.5 md:flex-row md:justify-between md:gap-1 text-[10px] md:text-xs';
